<template>
  <div>
    <div class="uk-width-xxlarge uk-margin-auto">
      <div v-if="status === 0" class="uk-alert-warning" uk-alert>
        <div class="uk-text-center">
          <p>
            Your account is <b>suspended</b>. You can use the CRM but you wil not receive any new appointments.<br>
            If you are not sure why this happened, please contact us.<br>
          </p>
          <p class="uk-margin-remove-bottom">
            <router-link :to="{ name: 'ticket-create' }" class="uk-button uk-button-small uk-button-secondary">Submit a Ticket</router-link>
          </p>
        </div>
      </div>
    </div>

    <div class="uk-card uk-card-default uk-margin">
      <div class="uk-card-header">
        <p class="uk-text-center uk-text-bold">Appointments for last 7 days</p>
      </div>
      <div class="uk-card-body uk-padding-remove">
        <apexchart height="300" type="bar" :options="lineChart.chartOptions" :series="lineChart.series" />
      </div>
    </div>

    <div class="uk-card uk-card-default">
      <div class="uk-card-header">
        <p class="uk-text-center uk-text-bold">New Appointments</p>
      </div>
      <div class="uk-card-body uk-padding-remove">
        <div v-if="appointments.length">
          <vue-good-table
            :columns="columns"
            :rows="appointments"
            :pagination-options="$globals.vgt.pagination"
            :row-style-class="rowStyleClassFn"
            style-class="uk-table uk-table-middle uk-table-divider uk-table-hover uk-margin-remove datatable dt-appointments"
            @on-row-click="onRowClick"
          >
            <template slot="table-column" slot-scope="props">
              <div v-html="formatLabel(props.column.compositeLabel, props.column.label)" />
            </template>

            <template slot="table-row" slot-scope="props">
              <template v-if="props.column.field == 'appointmentTimestamp'">
                <div :uk-tooltip="`title: ${$moment(props.row.appointmentTimestamp, 'X').fromNow()}`">
                  <p>{{ $moment(props.row.appointmentTimestamp, 'X').format('DD/MM/YYYY') }}</p>
                  <p>{{ $moment(props.row.appointmentTimestamp, 'X').format('HH:mm:ss') }}</p>
                </div>
              </template>

              <template v-else-if="props.column.field == 'appointmentDate'">
                <div :uk-tooltip="`title: ${$helpers.timeBits.find(bit => bit.id === props.row.appointmentDate.bit).time.long}`">
                  <p>{{ $moment(props.row.appointmentDate.date, 'YYMMDD').format('DD/MM/YYYY') }}</p>
                  <p>{{ $helpers.timeBits.find(bit => bit.id === props.row.appointmentDate.bit).time.short }}</p>
                </div>
              </template>

              <template v-else>
                {{ props.formattedRow[props.column.field] }}
              </template>
            </template>
          </vue-good-table>
        </div>
        <div v-else class="uk-padding-large uk-text-center">
          There are no new appointments!
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ComponentDashboardDentist',

  data() {
    return {
      columns: [
        // Customer Name
        {
          compositeLabel: true,
          label: [
            {
              value: 'Customer',
              small: true
            },
            {
              value: 'Name',
              small: false
            }
          ],
          field: 'name',
          thClass: 'uk-text-center',
          tdClass: 'uk-text-center',
          sortable: false
        },
        // Customer Phone
        {
          compositeLabel: true,
          label: [
            {
              value: 'Customer',
              small: true
            },
            {
              value: 'Phone',
              small: false
            }
          ],
          field: 'phone',
          thClass: 'uk-text-center',
          tdClass: 'uk-text-center',
          sortable: false
        },
        // Customer Email
        {
          compositeLabel: true,
          label: [
            {
              value: 'Customer',
              small: true
            },
            {
              value: 'Email',
              small: false
            }
          ],
          field: 'email',
          thClass: 'uk-text-center',
          tdClass: 'uk-text-center',
          sortable: false
        },
        // Appointment Timestamp
        {
          compositeLabel: true,
          label: [
            {
              value: 'Appointment',
              small: true
            },
            {
              value: 'Created',
              small: false
            }
          ],
          field: 'appointmentTimestamp',
          type: 'number',
          thClass: 'uk-text-center',
          tdClass: 'uk-text-center',
          sortable: false
        },
        // Appointment Date
        {
          compositeLabel: true,
          label: [
            {
              value: 'Appointment',
              small: true
            },
            {
              value: 'Date',
              small: false
            }
          ],
          field: 'appointmentDate',
          type: 'number',
          thClass: 'uk-text-center',
          tdClass: 'uk-text-center',
          sortable: false
        }
      ]
    }
  },

  computed: {
    lineChart() {
      const source = this.$store.state.dentists.collection.availability
      const start = this.$moment().subtract(6, 'day')
      const end = this.$moment()
      // Get range of last 7 dates with moment and moment-range
      const range = this.$moment.range(start, end)
      // Convert range to Array
      const rangeArray = Array.from(range.by('day'))
      // Format Array data to match data structure
      const dates = rangeArray.map((v) => {
        const data = source && source[v.format('YYMMDD')] ? Object.values(source[v.format('YYMMDD')]) : []

        return {
          title: v.format('DD/MM/YYYY'),
          appointments: data.reduce((n, x) => n + (x === 2), 0),
          availability: data.length
        }
      })

      return {
        chartOptions: {
          chart: {
            id: 'week',
            toolbar: { show: false },
            stacked: true
          },
          dataLabels: {
            enabled: false
          },
          stroke: {
            curve: 'smooth'
          },
          xaxis: {
            categories: dates.map((a) => a.title)
          },
          yaxis: {
            labels: {
              // Display as integer values if the span (difference bewteen lowest and highest value) is too low
              // Apexcharts will display floats by default
              // Value is formatted toFixed = 0, this will format the value from 'x.0' to 'x'
              formatter(val) {
                return val.toFixed()
              }
            }
          }
        },
        series: [{
          name: 'Appointments',
          data: dates.map((a) => a.appointments)
        }, {
          name: 'Availability',
          data: dates.map((a) => a.availability)
        }]
      }
    },

    appointments() {
      const source = this.$store.getters['appointments/new']

      const response = []

      source.forEach((item) => {
        const dentistData = item.dentist.data[this.$store.state.account.profile.dentist]

        response.push({
          id: item.id,
          name: `${item.customer.name.first} ${item.customer.name.last}`,
          phone: item.customer.contact.phone,
          email: item.customer.contact.email,
          appointmentTimestamp: item.info.created.timestamp.seconds,
          appointmentDate: dentistData,
          read: dentistData.read
        })
      })

      return response.reverse()
    },

    status() {
      return this.$store.getters['dentists/find'](this.$store.state.account.profile.dentist).status
    }
  },

  methods: {
    formatLabel(composite, data) {
      let response = null

      if (composite) {
        response = `
          <div class="label">
            <p class="${(data[0].small) ? 'small' : ''}">${data[0].value}</p>
            <p class="${(data[1].small) ? 'small' : ''}">${data[1].value}</p>
          </div>
        `
      }

      return response
    },

    rowStyleClassFn(row) {
      return row.read ? '' : 'new'
    },

    onRowClick(params) {
      this.$router.push({ name: 'appointment-single', params: { id: params.row.id } })
    }
  }
}
</script>
